import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import { RevenueDynamicsSkeleton } from './RevenueDynamicsSkeleton';
import { RevenueDynamicsView } from './RevenueDynamicsView';
// The domain model, not a page-local type: `revenueDynamicsMeta` holds the
// presentational config (KPI slots, bucket labels, the product picker's
// fixtures) and never exported a `RevenueDynamics`. The sibling hook and view
// both take it from `@/api/models`, and this is the shape `useRevenueDynamics`
// actually returns.
import type { RevenueDynamics as RevenueDynamicsModel } from '@/api/models';
import { useRevenueDynamics } from './useRevenueDynamics';

type RevenueDynamicsContentProps = {
    data: RevenueDynamicsModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    productId: string | null;
    setProductId: (id: string | null) => void;
};

/**
 * Self-served Revenue Dynamics widget. Owns its own data hook plus a
 * local product filter, wrapped in an `ErrorBoundary` so a render-time
 * failure cannot crash the Sales page.
 *
 * The series is live — `useRevenueDynamics` reads the endpoint, which
 * pre-buckets and pre-aggregates. The *product picker's options* are not:
 * there is no product-list endpoint for it yet, so it is seeded from
 * `MOCK_REVENUE_DYNAMICS_PRODUCTS`. That is the one fixture left on this page.
 */
export const RevenueDynamics: React.FC = () => {
    const { data, error, isLoading, onRetry, productId, setProductId } =
        useRevenueDynamics();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.revenueDynamics.errorTitle'),
            errorMessage: t('sales.widgets.revenueDynamics.errorMessage'),
            errorRetry: t('sales.widgets.revenueDynamics.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="revenue-dynamics-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="revenue-dynamics-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <RevenueDynamicsContent
                data={data}
                error={error}
                isLoading={isLoading}
                productId={productId}
                setProductId={setProductId}
            />
        </ErrorBoundary>
    );
};

const RevenueDynamicsContent: React.FC<RevenueDynamicsContentProps> = ({
    data,
    error,
    isLoading,
    productId,
    setProductId,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <RevenueDynamicsSkeleton />;
    }

    return (
        <RevenueDynamicsView
            data={data}
            productId={productId}
            setProductId={setProductId}
        />
    );
};
